import { useEffect, useState } from "react";
import api, { formatApiError, API } from "@/lib/api";
import { PageHero } from "@/components/common";
import { HelpLink } from "@/components/HelpHint";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Upload, Trash2, FileDown, ShieldCheck, Users, Bell, ChevronDown } from "lucide-react";
import { toast } from "sonner";

function SectionAcks() {
  const [data, setData] = useState([]);
  const [open, setOpen] = useState({});
  const load = () => api.get("/reports/section-acks").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const chase = async (id) => { try { await api.post(`/policies/${id}/remind`); toast.success("Reminder sent to those outstanding"); } catch { toast.error("Could not send reminders"); } };
  const totalPending = data.reduce((n, s) => n + s.policies.reduce((m, p) => m + p.pending, 0), 0);
  if (data.length === 0) return null;
  return (
    <div className="bg-white border border-slate-200 rounded-xl shadow-sm" data-testid="section-ack-report">
      <div className="px-5 py-3 border-b border-slate-100 flex items-center gap-2">
        <Users size={16} className="text-[#2EA3FD]" />
        <h3 className="font-semibold text-[#0B1A2F] text-sm">Acknowledgement tracker by section</h3>
        {totalPending > 0 && <span className="text-[10px] bg-amber-100 text-amber-700 rounded-full px-2 py-0.5">{totalPending} outstanding</span>}
      </div>
      <div className="divide-y divide-slate-100">
        {data.map((sec) => (
          <div key={sec.section} className="px-5 py-3" data-testid={`ack-section-${sec.section.replace(/\s+/g, "-").toLowerCase()}`}>
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">{sec.section}</p>
            <div className="space-y-2">
              {sec.policies.map((p) => {
                const pct = p.eligible ? Math.round((p.acknowledged / p.eligible) * 100) : 100;
                const isOpen = open[p.policy_id];
                return (
                  <div key={p.policy_id} className="rounded-lg border border-slate-100 p-3" data-testid={`ack-policy-${p.policy_id}`}>
                    <div className="flex items-center justify-between gap-3">
                      <button className="text-left min-w-0 flex items-center gap-1.5" onClick={() => setOpen((o) => ({ ...o, [p.policy_id]: !o[p.policy_id] }))}>
                        <ChevronDown size={14} className={`text-slate-400 transition-transform ${isOpen ? "rotate-180" : ""}`} />
                        <span className="font-medium text-[#0B1A2F] truncate">{p.title}</span>
                        {p.overdue && <span className="text-[10px] bg-rose-100 text-rose-700 rounded-full px-2 py-0.5 shrink-0">Overdue</span>}
                        {!p.overdue && p.ack_due_date && p.pending > 0 && <span className="text-[10px] bg-amber-100 text-amber-700 rounded-full px-2 py-0.5 shrink-0">Due {p.ack_due_date}</span>}
                      </button>
                      <div className="flex items-center gap-3 shrink-0">
                        <span className="text-xs text-slate-500">{p.acknowledged}/{p.eligible}</span>
                        <div className="w-24 h-2 rounded-full bg-slate-100 overflow-hidden"><div className="h-full bg-emerald-500" style={{ width: `${pct}%` }} /></div>
                        {p.pending > 0 && <Button size="sm" variant="outline" className="h-7 text-[11px]" onClick={() => chase(p.policy_id)} data-testid={`ack-chase-${p.policy_id}`}><Bell size={12} className="mr-1" />Chase {p.pending}</Button>}
                      </div>
                    </div>
                    {isOpen && p.pending_names.length > 0 && <p className="text-xs text-amber-600 mt-2 pl-5">Outstanding: {p.pending_names.join(", ")}{p.pending > p.pending_names.length ? "…" : ""}</p>}
                    {isOpen && p.pending === 0 && <p className="text-xs text-emerald-600 mt-2 pl-5">Everyone eligible has acknowledged.</p>}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function DocumentsPolicies() {
  const [rows, setRows] = useState([]);
  const [title, setTitle] = useState("");
  const [requiresAck, setRequiresAck] = useState(true);
  const [file, setFile] = useState(null);
  const [scopeType, setScopeType] = useState("all");
  const [roles, setRoles] = useState([]);
  const [depts, setDepts] = useState([]);
  const [allDepts, setAllDepts] = useState([]);
  const [sections, setSections] = useState([]);
  const [sectionId, setSectionId] = useState("none");
  const [ackDue, setAckDue] = useState("");
  const ROLE_OPTS = [["employee", "Employees"], ["manager", "Managers"], ["hr_admin", "HR Admins"], ["admin", "Admins"]];
  const load = () => api.get("/policies").then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); api.get("/employees").then((r) => setAllDepts([...new Set(r.data.map((e) => e.department).filter(Boolean))])).catch(() => {}); api.get("/sections").then((r) => setSections(r.data)).catch(() => {}); }, []);
  const toggle = (arr, set, v) => set(arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v]);
  const upload = async () => {
    if (!title.trim() || !file) { toast.error("Add a title and choose a file"); return; }
    const fd = new FormData();
    fd.append("title", title); fd.append("requires_ack", requiresAck); fd.append("file", file);
    fd.append("scope_type", scopeType); fd.append("roles", roles.join(",")); fd.append("departments", depts.join(","));
    fd.append("section_id", sectionId === "none" ? "" : sectionId);
    fd.append("ack_due_date", ackDue);
    try { await api.post("/policies", fd); toast.success("Policy published"); setTitle(""); setFile(null); setScopeType("all"); setRoles([]); setDepts([]); setSectionId("none"); setAckDue(""); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const sectionName = (id) => sections.find((s) => s.id === id)?.name;
  const openDoc = async (id) => { try { const r = await api.get(`/documents/${id}/file`, { responseType: "blob" }); window.open(URL.createObjectURL(r.data), "_blank"); } catch { toast.error("Not available"); } };
  const del = async (id) => { try { await api.delete(`/policies/${id}`); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const scopeLabel = (s) => !s || s.type === "all" ? "Everyone" : s.type === "roles" ? `Roles: ${(s.roles || []).join(", ")}` : `Depts: ${(s.departments || []).join(", ")}`;
  const Chip = ({ on, onClick, children, tid }) => <button type="button" onClick={onClick} className={`text-xs rounded-full px-2.5 py-1 border ${on ? "bg-[#2EA3FD] text-white border-[#2EA3FD]" : "bg-white text-slate-600 border-slate-200"}`} data-testid={tid}>{children}</button>;

  return (
    <div className="space-y-5" data-testid="people-documents-page">
      <PageHero title="Documents & Policies" subtitle="Publish company-wide policies, control who must acknowledge, and track who has." icon={ShieldCheck} tone="teal" pageKey="documents">
        <HelpLink article="record-documents" tone="dark" testid="documents-help-link" />
        <Button size="sm" className="bg-white text-[#0B1A2F] hover:bg-slate-100" onClick={() => window.open(`${API}/reports/policy-acks?format=csv`, "_blank")} data-testid="export-acks-btn"><FileDown size={15} className="mr-2" />Export acknowledgements (CSV)</Button>
      </PageHero>

      <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm space-y-3">
        <div className="flex items-end gap-3 flex-wrap">
          <div className="flex-1 min-w-[12rem]"><Label className="text-xs text-slate-500">Policy title</Label><Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Code of Conduct" data-testid="policy-title-input" /></div>
          <label className="flex items-center gap-2 text-sm text-slate-600 pb-2"><Switch checked={requiresAck} onCheckedChange={setRequiresAck} data-testid="policy-ack-toggle" />Requires acknowledgement</label>
          <input type="file" onChange={(e) => setFile(e.target.files?.[0] || null)} className="text-xs" data-testid="policy-file-input" />
        </div>
        <div>
          <Label className="text-xs text-slate-500">Who must acknowledge</Label>
          <div className="flex items-center gap-2 mt-1 flex-wrap">
            <Select value={scopeType} onValueChange={(v) => { setScopeType(v); setRoles([]); setDepts([]); }}><SelectTrigger className="w-44" data-testid="policy-scope-select"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="all">Everyone</SelectItem><SelectItem value="roles">Specific roles</SelectItem><SelectItem value="departments">Specific departments</SelectItem></SelectContent></Select>
            {scopeType === "roles" && <div className="flex gap-1.5 flex-wrap">{ROLE_OPTS.map(([v, l]) => <Chip key={v} on={roles.includes(v)} onClick={() => toggle(roles, setRoles, v)} tid={`policy-role-${v}`}>{l}</Chip>)}</div>}
            {scopeType === "departments" && <div className="flex gap-1.5 flex-wrap">{allDepts.length === 0 ? <span className="text-xs text-slate-400">No departments found</span> : allDepts.map((dp) => <Chip key={dp} on={depts.includes(dp)} onClick={() => toggle(depts, setDepts, dp)} tid={`policy-dept-${dp}`}>{dp}</Chip>)}</div>}
          </div>
        </div>
        <div>
          <Label className="text-xs text-slate-500">Section (files it in My HR)</Label>
          <Select value={sectionId} onValueChange={setSectionId}>
            <SelectTrigger className="w-64" data-testid="policy-section-select"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="none">Uncategorised</SelectItem>{sections.map((s) => <SelectItem key={s.id} value={s.id}>{s.parent_id ? "— " : ""}{s.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        {requiresAck && (
          <div>
            <Label className="text-xs text-slate-500">Acknowledgement due date (optional — auto-chases as it approaches)</Label>
            <Input type="date" value={ackDue} onChange={(e) => setAckDue(e.target.value)} className="w-52" data-testid="policy-ackdue-input" />
          </div>
        )}
        <Button className="bg-[#2EA3FD] hover:bg-[#1B8EE6]" onClick={upload} data-testid="policy-upload-btn"><Upload size={15} className="mr-2" />Publish</Button>
      </div>

      {rows.length === 0 ? <p className="text-sm text-slate-400">No policies published yet.</p> : (
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm divide-y divide-slate-100">
          {rows.map((p) => (
            <div key={p.id} className="flex items-center justify-between gap-3 px-5 py-3" data-testid={`policy-row-${p.id}`}>
              <button onClick={() => openDoc(p.id)} className="text-left min-w-0 flex items-center gap-2">
                <span className="h-8 w-8 shrink-0 rounded-lg bg-[#EFF8FF] text-[#2EA3FD] grid place-items-center"><ShieldCheck size={16} /></span>
                <span><span className="font-medium text-[#0B1A2F] hover:text-[#2EA3FD]">{p.title}</span><p className="text-xs text-slate-500">{sectionName(p.section_id) ? `${sectionName(p.section_id)} · ` : ""}{scopeLabel(p.scope)}{p.requires_ack ? ` · ${p.ack_count || 0} acknowledged` : " · info only"}{p.ack_due_date ? ` · due ${p.ack_due_date}` : ""}</p></span>
              </button>
              <Button variant="ghost" size="sm" className="text-rose-600" onClick={() => del(p.id)} data-testid={`policy-del-${p.id}`}><Trash2 size={14} /></Button>
            </div>
          ))}
        </div>
      )}

      <SectionAcks />
    </div>
  );
}
